import React from 'react';
import styled from 'styled-components';
import clamp from 'lodash/clamp';
import LinearProgress from '@mui/material/LinearProgress';
import { InformationalCallOut } from '@grnhse/seedling/lib/birch/components/call_out';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { withLocalePrefix } from 'shared/utils/translation';
import {
  type RankingPhase,
  type RankingProgress,
} from 'talent_matching/my_greenhouse/hooks/use_my_greenhouse_candidates';

const t = withLocalePrefix('talent_matching.my_greenhouse.ranking_progress');

const GATHERING_VALUE = 15;
export const MAX_PROGRESS_VALUE = 90;

const PHASE_LABEL_KEYS: Record<RankingPhase, string> = {
  gathering: 'gathering',
  ranking: 'ranking',
  finalizing: 'finalizing',
};

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
`;

const ProgressBarContainer = styled.div`
  flex: 1;
  min-width: 120px;
`;

const StyledLinearProgress = styled(LinearProgress)`
  height: 8px;
  border-radius: 8px;
`;

const PercentLabel = styled(Body)`
  min-width: 36px;
  text-align: right;
`;

const phaseLabel = (phase: RankingPhase): string => t(PHASE_LABEL_KEYS[phase]);

export const computeProgressValue = (progress: RankingProgress): number => {
  switch (progress.phase) {
    case 'gathering':
      return GATHERING_VALUE;
    case 'ranking': {
      const total = progress.totalChunks ?? 0;
      const completed = progress.completedChunks ?? 0;
      if (total <= 0) {
        return GATHERING_VALUE;
      }
      const ratio = clamp(completed / total, 0, 1);
      return GATHERING_VALUE + ratio * (MAX_PROGRESS_VALUE - GATHERING_VALUE);
    }
    case 'finalizing':
      return MAX_PROGRESS_VALUE;
  }
};

type Props = {
  progress: RankingProgress;
};

const RankingProgressBanner = ({ progress }: Props) => {
  const value = computeProgressValue(progress);
  const percent = Math.round(value);

  return (
    <InformationalCallOut
      data-provides="my-greenhouse-ranking-progress"
      bodyText={
        <Stack spacing={1} sx={{ width: '100%' }}>
          <Body medium>{phaseLabel(progress.phase)}</Body>
          <ProgressRow>
            <ProgressBarContainer>
              <StyledLinearProgress
                variant="determinate"
                value={value}
                aria-label={phaseLabel(progress.phase)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
              />
            </ProgressBarContainer>
            <PercentLabel>{`${percent}%`}</PercentLabel>
          </ProgressRow>
        </Stack>
      }
    />
  );
};

export default RankingProgressBanner;
